import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  EdgeProps as FlowEdgeProps,
  ReactFlowInstance,
  ReactFlowProps,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import { mapValues } from 'lodash-es';
import 'reactflow/dist/style.css';
import styled from 'styled-components';

import { hideSidebar } from '@components/sidebar';
import { theme } from '@styles/theme';
import { os } from '@utils/operating-system';

import { JourneyForm } from 'api/journey-forms/requests';
import { PollingConfigList } from 'api/journey-polling/requests';
import { JourneyWebhookList } from 'api/journey-webhooks/requests';

import { Controls } from './controls';
import { useAnimatedNodes } from './hooks/use-animated-nodes';

export type EdgeProps = FlowEdgeProps;

export type BaseBuilderProps = Pick<ReactFlowProps, 'edgeTypes'> & {
  nodeTypes: { [key in string]: { Node: ReactNode } };
  className?: string;
  isDragging?: boolean;
  journeyId?: string;
  isTemplate?: boolean;
};

export const zoomDuration = 250;

type TriggerContextType = {
  forms: JourneyForm[];
  webhooks: JourneyWebhookList[];
  pollingConfigs: PollingConfigList[];
  triggersCount: number;
  journeyId?: string;
};

export const TriggerContext = createContext<TriggerContextType | undefined>(undefined);

export const useTriggerContext = () => {
  const context = useContext(TriggerContext);
  if (!context) {
    throw new Error('useTriggerContext must be used within a TriggerContextProvider');
  }
  return context;
};

export const BaseBuilder = ({
  nodeTypes,
  isDragging,
  journeyId,
  isTemplate,
  ...rest
}: BaseBuilderProps) => {
  const [nodes, , onNodesChange] = useNodesState([]);
  const [edges, , onEdgesChange] = useEdgesState([]);
  const { animatedNodes } = useAnimatedNodes(nodes);
  const [isPanningKey, setIsPanningKey] = useState(false);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);

  const nodeComponents = useRef(mapValues(nodeTypes, (value) => value.Node));

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = os === 'MacOS' ? event.metaKey : event.ctrlKey;
      if (event.key === ' ') {
        setIsPanningKey(true);
      }
      if (key && (event.key === '=' || event.key === '+')) {
        event.preventDefault();
        reactFlowInstance.current?.zoomIn({ duration: zoomDuration });
      }
      if (key && event.key === '-') {
        event.preventDefault();
        reactFlowInstance.current?.zoomOut({ duration: zoomDuration });
      }
      if (key && (event.key === '0' || event.key === 'Home')) {
        event.preventDefault();
        reactFlowInstance.current?.fitView({ duration: zoomDuration, maxZoom: 1 });
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === ' ') {
        setIsPanningKey(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  return (
    <StyledReactFlow
      ref={reactFlowWrapper}
      onInit={(instance) => (reactFlowInstance.current = instance)}
      deleteKeyCode={null}
      nodes={animatedNodes}
      // TS complaining that we use JourneyNode instead of NodeProps from reactflow as parameter type
      // for our nodes components
      // @ts-ignore-next-line
      nodeTypes={nodeComponents.current}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodesDraggable={false}
      nodesConnectable={false}
      zoomActivationKeyCode={['Control', 'Meta']}
      multiSelectionKeyCode={null}
      selectionKeyCode={null}
      fitView
      fitViewOptions={{ maxZoom: 1 }}
      proOptions={{ hideAttribution: true }}
      style={{ background: theme.color.uiTrans5 }}
      maxZoom={2}
      minZoom={0.3}
      zoomOnScroll={false}
      panOnDrag={[1, 2]}
      panOnScroll
      $isPanningKey={isPanningKey}
      defaultEdgeOptions={{
        type: 'defaultEdge',
      }}
      onPaneClick={hideSidebar}
      {...rest}
    >
      <Background />
      {nodes.length !== 0 && <Controls journeyId={journeyId} isTemplate={isTemplate} />}
    </StyledReactFlow>
  );
};

const StyledReactFlow = styled(ReactFlow)<{ $isPanningKey: boolean }>(
  ({ $isPanningKey = false }) => ({
    '.react-flow__pane': {
      cursor: $isPanningKey ? 'grab' : 'default',
    },

    '.react-flow__pane.dragging': {
      cursor: 'grabbing',
    },
  })
);
